<template>
  <base-dropdown
    :disabled="!items.length"
    :shown="focused"
    @hidden="handleReset(); focused = false"
    @focus="focused = true"
    @blur="handleBlur"
  >
    <template #default="{ show }">
      <base-input
        v-bind="$attrs"
        :id="id"
        :value="displayedValue"
        maxlength="5"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :readonly="readonly"
        :invalid="invalid"
        :class="$style.timeInput"
        autocomplete="off"
        @input="
          displayedValue = $event;
          handleOpen();
          handleInput($event);
        "
        @click="handleOpen"
        @beforeinput="handleBeforeInput"
        @keydown.down.exact.prevent="show(); handleKey()"
        @keydown.up.exact.prevent="show(); handleKey()"
        @keydown.enter.exact="handleEnter"
      />
    </template>
    <template #content>
      <dropdown-menu-layout :size="dropdownSize">
        <template #content>
          <base-list
            ref="list"
            :gap="0"
            :active-index="selectedIndex"
            :items="selectableItems"
            :class="$style.list"
          >
            <template #item="{ item, index }">
              <base-select-item
                :class="$style.item"
                :show-checkbox="false"
                :title="item.name"
                :hovered="item.userSelection.hovered"
                :selected="item.userSelection.selected"
                @click="toggle(item)"
                @mouseenter="onHover(index)"
              >
                <template #title-hint>
                  <span
                    v-if="item.hint"
                    :class="$style.hint"
                  >
                    ({{ i18n(item.hint.name, { time: item.hint.time }) }})
                  </span>
                </template>
              </base-select-item>
            </template>
          </base-list>
        </template>
      </dropdown-menu-layout>
    </template>
  </base-dropdown>
</template>

<script setup lang="ts">
import { nanoid } from 'nanoid';
import { computed, onMounted, ref, toRefs, watch } from 'vue';
import BaseDropdown from '../../legacy/ui/base-dropdown/base-dropdown.vue';
import { BaseList, BaseSelectItem } from '../../ui/base-list';
import DropdownMenuLayout from '../../legacy/ui/dropdown-menu-layout/dropdown-menu-layout.vue';
import { useListSelect } from '../../lib/list-selection';
import { useI18N } from '../../legacy/lib/use-i18n';
import BaseInput from '../../legacy/ui/base-input/base-input.vue';
import { useTimePicker } from './useTimePicker';

const { id = nanoid(), ...props } = defineProps<{
  value?: string;
  min?: string;
  name?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  invalid?: boolean;
}>();
const emit = defineEmits<{
  change: [value: string];
}>();

const { value, min } = toRefs(props);

const i18n = useI18N();

// View variables
const focused = ref(false);
const displayedValue = ref('');
const inserting = ref(false);

const dropdownSize = computed(() => {
  return min.value ? 'smaller' : 'date';
});

// Time picker logic
const { parseInput, selected, items } = useTimePicker({
  modelValue: value,
  minTime: min
});

const selectedIndex = computed(() => {
  return items.value.findIndex((item) => item.id === selected.value);
});

// Select behavior

const { selectableItems, onHover, toggle, hoveredIndex } = useListSelect({
  items,
  required: true,
  modelValue: selected.value,
  onChange: (value) => {
    inserting.value = false;
    focused.value = false;
    emit('change', value!);
  },
  useKeyboardEvents: focused
});

onMounted(() => {
  onHover(selectedIndex.value);
});

// Handlers

const handleOpen = () => {
  focused.value = true;
  if (hoveredIndex.value === null) {
    onHover(2);
  }
};

const handleKey = () => {
  if (!focused.value) {
    handleOpen();
  }

  setTimeout(() => {
    if (hoveredIndex.value !== null) {
      emit('change', items.value[hoveredIndex.value].name);
    }
  }, 0);
};

const handleReset = () => {
  inserting.value = false;
  displayedValue.value = selected?.value || '';
};

const handleBeforeInput = (event) => {
  if (!event.data) {
    return;
  }
  if (!/[\d:]/g.test(event.data)) {
    event.preventDefault();
  }
};
const handleInput = (input: string) => {
  inserting.value = true;
  try {
    const time = parseInput(input);
    const idx = items.value.findIndex((e) => e.name === time);
    onHover(idx);

    emit(
      'change',
      time
    );
  } catch {}
};

const handleBlur = () => {
  // focused.value = false;
  handleReset();
};

const handleEnter = (event: KeyboardEvent) => {
  if (!focused.value) {
    return;
  }
  event.preventDefault();
  focused.value = false;
};

watch(selected, () => {
  if (!inserting.value) {
    handleReset();
  }
}, {
  immediate: true
});
</script>

<style module>
.timeInput {
  width: 80px;
}

.list{
  display: grid;
  padding: 8px;
  max-height: 210px;
}
.item {
  width: 100%;
}

.hint{
  font-size: 16px;
  color: $black-60;
}
</style>

<i18n lang="json">
{
  "min": {
    "ru_RU": "{time} мин",
    "en_US": "{time} min"
  },
  "hr": {
    "ru_RU": "{time} ч",
    "en_US": "{time} hr"
  }
}
</i18n>
